import type { ReactNode } from 'react';

import { Card, Skeleton } from '@/design-system';
import { errorMessage } from '@/shared/api';
import { ErrorState } from '@/shared/ui';

/**
 * Eén kolom kaarten, even breed als de tabbladen met twee kolommen (Huisstijl,
 * Rapport), zodat elk tabblad dezelfde rand rechts heeft. Op 780 px bleef er op
 * een breed scherm een baan witruimte naast de kaarten staan; de velden binnen
 * een kaart verdelen zich nu over de breedte.
 */
export const TAB_MAX_WIDTH = 'max-w-[1120px]';

export function TabColumn({ children }: { children: ReactNode }) {
    return <div className={`flex ${TAB_MAX_WIDTH} flex-col gap-[14px]`}>{children}</div>;
}

export interface SettingsSectionProps {
    title: string;
    query: { isPending: boolean; isError: boolean; error: unknown };
    onRetry: () => void;
    children: ReactNode;
}

/**
 * Elke kaart draagt zijn eigen wachtstand. Eén mislukte query mag niet het hele
 * tabblad wegnemen: de rest blijft gewoon bruikbaar.
 */
export function SettingsSection({ title, query, onRetry, children }: SettingsSectionProps) {
    if (query.isPending) {
        return <LoadingCard label={`${title} laden`} />;
    }

    if (query.isError) {
        return (
            <Card padding="lg">
                <ErrorState error={query.error} title={title} onRetry={onRetry} />
            </Card>
        );
    }

    return <>{children}</>;
}

/** De wachtstand van een kaart: een kop en twee regels. */
export function LoadingCard({ label }: { label: string }) {
    return (
        <Card padding="lg" aria-busy="true">
            <Skeleton width="45%" height={15} label={label} />
            <Skeleton height={14} className="mt-3" />
            <Skeleton width="70%" height={14} className="mt-2" />
        </Card>
    );
}

/**
 * Een schakelaar springt meteen om en draait terug als de server hem weigert
 * (`useUpdateSettings`); deze regel zegt dan waarom.
 */
export function SettingsSaveError({ error }: { error: unknown }) {
    if (!error) {
        return null;
    }

    return (
        <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
            {errorMessage(error)}
        </p>
    );
}
